import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, TOKEN_KEY, useAuth } from '../contexts/AuthContext';
import { useSala } from '../contexts/SalaContext';
import useAudioParticipante from '../hooks/useAudioParticipante';
import { GAP, calcularGrade, calcularDestaque } from '../helpers/gradeVideo';
import { lerMarcaDeLeitura, gravarMarcaDeLeitura, contarNaoLidas, ultimoId } from '../helpers/chatLeitura';
import { CORES_DA_SALA, estiloDaCor } from '../helpers/coresDaSala';
import { tocar } from '../helpers/salaAudio';

const INTERVALO_PRESENCA_MS = 5000;

// Mesma convencao do /ti/atendimentos (ver CONTEXTO): hoje mostra so a hora,
// dias anteriores mostram data + hora.
function horaMensagem(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hoje = new Date();
  const mesmoDia = d.getDate() === hoje.getDate()
    && d.getMonth() === hoje.getMonth()
    && d.getFullYear() === hoje.getFullYear();
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (mesmoDia) return hora;
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
}

// Traducao do tipo de candidato ICE que fechou a conexao. E o dado que decide
// se um dia vale subir um TURN proprio: enquanto for "LAN direta" ou
// "via internet", nao precisa.
const ROTULO_ROTA = {
  host:  { texto: 'LAN direta', icone: 'bi-hdd-network', cor: '#4CB553' },
  srflx: { texto: 'via internet', icone: 'bi-globe', cor: '#2A8A9C' },
  prflx: { texto: 'via internet', icone: 'bi-globe', cor: '#2A8A9C' },
  relay: { texto: 'via TURN', icone: 'bi-arrow-repeat', cor: '#fe8d25' },
};

// A rota efetiva e a "pior" das duas pontas: se um lado precisou de relay,
// a chamada inteira esta passando por relay.
function classificarRota(rota) {
  if (!rota) return null;
  const tipos = [rota.local, rota.remoto].filter(Boolean);
  if (!tipos.length) return null;
  if (tipos.includes('relay')) return ROTULO_ROTA.relay;
  if (tipos.some((t) => t === 'srflx' || t === 'prflx')) return ROTULO_ROTA.srflx;
  return ROTULO_ROTA.host;
}

function iniciais(nome) {
  return String(nome || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join('')
    .toUpperCase();
}

// Mede um elemento e reage a qualquer mudanca de tamanho: janela, sidebar
// recolhendo/expandindo, zoom.
//
// Devolve uma *callback ref*, nao um useRef. E proposital: o elemento medido
// (.sala-palco) so existe depois que a pessoa entra na sala — no primeiro
// render a pagina esta no lobby. Com useRef + useLayoutEffect([]) o efeito
// rodaria uma unica vez, com ref.current ainda null, e o ResizeObserver nunca
// observaria nada (a grade ficaria eternamente sem medida).
function useTamanho() {
  const [no, setNo] = useState(null);
  const [tamanho, setTamanho] = useState({ largura: 0, altura: 0 });

  useLayoutEffect(() => {
    if (!no) return undefined;

    // Medicao inicial SINCRONA, alem do observer.
    //
    // O ResizeObserver so entrega durante um passo de renderizacao, e uma
    // pagina que nao esta renderizando (aba aberta em segundo plano, janela
    // minimizada) nao tem nenhum. Sem esta leitura, quem abrisse a sala numa
    // aba de fundo ficaria com a grade zerada — e os tiles com display:none,
    // ou seja, tela preta — ate focar a aba.
    const inicial = no.getBoundingClientRect();
    if (inicial.width > 0 && inicial.height > 0) {
      setTamanho({ largura: inicial.width, altura: inicial.height });
    }

    const observer = new ResizeObserver(([entrada]) => {
      const { width, height } = entrada.contentRect;
      setTamanho((prev) => (
        Math.abs(prev.largura - width) < 1 && Math.abs(prev.altura - height) < 1
          ? prev
          : { largura: width, altura: height }
      ));
    });
    observer.observe(no);
    return () => observer.disconnect();
  }, [no]);

  return [setNo, tamanho];
}

// Ajustes de audio por participante (volume e gate) vivem no SalaProvider
// (ver helpers/salaAudio + contexts/SalaContext) — o SalaAudioSink precisa
// dos mesmos ajustes para seguir aplicando volume fora de /ti/sala.

// O icone da sala virou EMOJI, mas quem personalizou antes tem uma classe do
// Bootstrap gravada. Este componente aceita os dois para nao quebrar nada.
function IconeSala({ icone, className = '' }) {
  const valor = icone || 'bi-person-circle';
  if (valor.startsWith('bi-')) {
    return <i className={`bi ${valor} ${className}`} />;
  }
  return <span className={`sala-emoji ${className}`} aria-hidden="true">{valor}</span>;
}

// Tile de video. O <video> so aceita MediaStream via propriedade (srcObject),
// nunca via atributo — por isso o efeito.
function VideoTile({
  tile, estilo, compacto, fixado, onFixar, saidaAudio, selo, audio, onAudio,
}) {
  const videoRef = useRef(null);
  const [painelAudio, setPainelAudio] = useState(false);
  const { stream, nome, local, tipo, mudo, cameraDesligada, conexao, rota } = tile;

  // Controle de audio so faz sentido em camera de outra pessoa: o proprio
  // tile e sempre mudo (eco) e o tile de tela nao carrega audio.
  const temAudio = !local && tipo === 'camera' && !!onAudio;
  const cfg = audio || { volume: 1, gate: false, sensibilidade: 50 };

  const { nivel, falando, ruidoIgnorado } = useAudioParticipante({
    elementoRef: videoRef,
    stream,
    volume: cfg.volume,
    gate: temAudio && cfg.gate,
    sensibilidade: cfg.sensibilidade,
    medindo: temAudio && painelAudio,
  });

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
    // Trocar a fonte nao retoma a reproducao sozinho — ver `tocar`.
    if (stream) tocar(el, nome);
  }, [stream, nome]);

  // Saida de audio escolhida pelo usuario (fone x alto-falante). setSinkId e
  // por ELEMENTO, entao cada tile aplica no seu <video>. Só existe no
  // Chrome/Edge; onde nao existe, o audio segue no dispositivo padrao do SO.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !saidaAudio || typeof el.setSinkId !== 'function') return;
    el.setSinkId(saidaAudio).catch(() => { /* id sumiu (fone desplugado) */ });
  }, [saidaAudio, stream]);

  const ehTela = tipo === 'tela';
  const semVideo = !ehTela && (cameraDesligada || !stream?.getVideoTracks?.().length);
  const conectando = !local && conexao !== 'connected';
  const rotaChip = !compacto && !ehTela ? classificarRota(rota) : null;

  return (
    <div
      className={[
        'sala-tile',
        ehTela ? 'sala-tile--tela' : '',
        compacto ? 'sala-tile--mini' : '',
        fixado ? 'sala-tile--fixado' : '',
        temAudio && cfg.gate && falando ? 'sala-tile--falando' : '',
      ].filter(Boolean).join(' ')}
      style={estilo}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        // O proprio audio nunca toca de volta: seria eco puro. A tela tambem
        // e sempre muda (o audio da pessoa vem pelo tile de camera).
        muted={local || ehTela}
        className={`sala-tile__video ${local && !ehTela ? 'sala-tile__video--espelhado' : ''}`}
        style={{ visibility: semVideo ? 'hidden' : 'visible' }}
      />

      {semVideo && (
        <div className="sala-tile__avatar">
          <span>{iniciais(nome)}</span>
        </div>
      )}

      {conectando && (
        <div className="sala-tile__conectando">
          <div className="screen-loader__spinner" />
          <span>{conexao === 'failed' ? 'Falha na conexão' : 'Conectando...'}</span>
        </div>
      )}

      <div className="sala-tile__acoes">
        {temAudio && (
          <button
            type="button"
            className={`sala-tile__acao ${cfg.gate || cfg.volume < 1 ? 'sala-tile__acao--on' : ''}`}
            onClick={() => setPainelAudio((v) => !v)}
            title={`Áudio de ${nome}`}
            aria-label={`Áudio de ${nome}`}
          >
            <i className={`bi ${cfg.volume === 0 ? 'bi-volume-mute-fill' : cfg.gate ? 'bi-soundwave' : 'bi-volume-up-fill'}`} />
          </button>
        )}
        <button
          type="button"
          className={`sala-tile__acao ${fixado ? 'sala-tile__acao--on' : ''}`}
          onClick={() => onFixar(tile.chave)}
          title={fixado ? 'Desafixar' : 'Fixar em destaque'}
          aria-label={fixado ? 'Desafixar' : 'Fixar em destaque'}
        >
          <i className={`bi ${fixado ? 'bi-pin-fill' : 'bi-pin-angle'}`} />
        </button>
      </div>

      {temAudio && painelAudio && (
        <div className="sala-audio" onClick={(e) => e.stopPropagation()} role="group" aria-label={`Áudio de ${nome}`}>
          <div className="sala-audio__linha">
            <i className="bi bi-volume-down" />
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(cfg.volume * 100)}
              onChange={(e) => onAudio(tile.usuarioId, { volume: Number(e.target.value) / 100 })}
              aria-label="Volume"
            />
            <span className="sala-audio__valor">{Math.round(cfg.volume * 100)}%</span>
          </div>

          <label className="sala-audio__opcao">
            <input
              type="checkbox"
              checked={cfg.gate}
              onChange={(e) => onAudio(tile.usuarioId, { gate: e.target.checked })}
            />
            <span>Ouvir só quando falar</span>
          </label>

          {cfg.gate && (
            <div className="sala-audio__linha sala-audio__linha--sens">
              <span className="sala-audio__rotulo">Sensibilidade</span>
              <input
                type="range"
                min="0"
                max="100"
                value={cfg.sensibilidade}
                onChange={(e) => onAudio(tile.usuarioId, { sensibilidade: Number(e.target.value) })}
                aria-label="Sensibilidade"
              />
            </div>
          )}

          <div className="sala-audio__medidor" title="Nível de som que está chegando">
            <div
              className={`sala-audio__nivel ${cfg.gate && falando ? 'sala-audio__nivel--aberto' : ''} ${ruidoIgnorado ? 'sala-audio__nivel--ruido' : ''}`}
              style={{ width: `${Math.round(nivel * 100)}%` }}
            />
          </div>
          <p className="sala-audio__dica">
            {!cfg.gate && 'Ajuste só para você — ninguém mais é afetado.'}
            {cfg.gate && falando && 'Passando o som'}
            {cfg.gate && !falando && (ruidoIgnorado
              ? 'Tem som chegando, mas não parece voz — ignorando'
              : 'Silenciado até falar')}
          </p>
        </div>
      )}

      <div className="sala-tile__rodape">
        <span className="sala-tile__nome">
          {ehTela && <i className="bi bi-display-fill" style={{ color: '#3CA8BC' }} />}
          {!ehTela && mudo && <i className="bi bi-mic-mute-fill" style={{ color: '#e05252' }} />}
          {nome}
        </span>
        {selo && (
          <span className="sala-tile__rota" title="Resolução que sua câmera está entregando de verdade">
            <i className="bi bi-aspect-ratio" />
            {selo}
          </span>
        )}
        {rotaChip && (
          <span className="sala-tile__rota" title="Como a mídia está trafegando entre vocês">
            <i className={`bi ${rotaChip.icone}`} style={{ color: rotaChip.cor }} />
            {rotaChip.texto}
          </span>
        )}
      </div>
    </div>
  );
}

// Coluna de salas dentro da chamada. Vertical de proposito: e onde o time
// passa o dia, e a lateral tem espaco sobrando enquanto a barra superior nao
// tinha. Geral no topo, pessoais abaixo.
//
// O hover de cada chip responde a pergunta que importa antes de entrar:
// "da para incomodar?" — status do dono + quem ja esta la dentro.
function BarraDeSalas({ salas, limite, salaAtiva, alvo, onIr, meuId }) {
  // Chips na HORIZONTAL, no topo. Ja foram uma coluna vertical a esquerda; o
  // time preferiu de volta na horizontal porque o que importa e a altura
  // sobrando para as cameras e a tela compartilhada, que sao o conteudo.
  //
  // O balao continua sendo renderizado FORA da barra, em position: fixed, com
  // as coordenadas medidas no hover: a barra tem overflow para rolar quando ha
  // muitas salas, e em CSS um eixo com overflow diferente de `visible` forca o
  // outro a virar `auto` — entao ela recorta e o balao sumiria.
  const [balao, setBalao] = useState(null);

  const mostrar = (sala, evento) => {
    const r = evento.currentTarget.getBoundingClientRect();
    // Abaixo do chip, alinhado a esquerda dele.
    setBalao({ sala, top: r.bottom + 8, left: r.left });
  };

  return (
    <>
      <nav className="sala-barra" aria-label="Salas">
        {salas.map((s) => {
          const ativa = s.id === (alvo || salaAtiva);
          const emTransito = s.id === alvo;
          const cheia = s.participantes.length >= limite && !ativa;
          const minha = s.pessoal && String(s.donoId) === String(meuId);

          return (
            <button
              key={s.id}
              type="button"
              className={[
                'sala-barra__botao',
                ativa ? 'sala-barra__botao--ativa' : '',
                s.cor ? 'sala-barra__botao--colorida' : '',
              ].filter(Boolean).join(' ')}
              style={estiloDaCor(s.cor)}
              onClick={() => onIr(s.id)}
              onMouseEnter={(e) => mostrar(s, e)}
              onFocus={(e) => mostrar(s, e)}
              onMouseLeave={() => setBalao(null)}
              onBlur={() => setBalao(null)}
              disabled={ativa || cheia || !!alvo}
            >
              {emTransito
                ? <i className="bi bi-arrow-repeat" />
                : <IconeSala icone={s.icone} />}
              <span className="sala-barra__nome">
                {s.nome}
                {minha && <span className="sala-barra__minha">você</span>}
              </span>
              <span className="sala-barra__contagem">
                {cheia ? 'cheia' : `${s.participantes.length}/${limite}`}
              </span>
            </button>
          );
        })}
      </nav>

      {balao && (
        <div className="sala-balao" style={{ top: balao.top, left: balao.left }} role="tooltip">
          <strong>{balao.sala.nome}</strong>
          {balao.sala.pessoal && (
            <span className={`sala-balao__status ${balao.sala.recado ? '' : 'sala-balao__status--vazio'}`}>
              <i className={`bi ${balao.sala.recado ? 'bi-circle-fill' : 'bi-circle'}`} />
              {balao.sala.recado || 'sem status'}
            </span>
          )}
          <span className="sala-balao__dentro">
            {balao.sala.participantes.length
              ? `Na sala: ${balao.sala.participantes.map((p) => p.nome).join(', ')}`
              : 'Ninguém na sala'}
          </span>
        </div>
      )}
    </>
  );
}

// Seletor rapido de status, na barra de controles. Sem modal: clica, escolhe,
// salva. Trocar de status e a coisa que mais se faz no dia, entao nao pode
// custar tres cliques e um formulario.
function StatusRapido({ atual, sugestoes, onEscolher }) {
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const escolher = async (valor) => {
    setSalvando(true);
    try {
      await onEscolher(valor);
      setAberto(false);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="sala-status">
      {aberto && (
        <>
          <div className="sala-status__fundo" onClick={() => setAberto(false)} role="presentation" />
          <div className="sala-status__lista" role="listbox">
            {(sugestoes || []).map((op) => (
              <button
                key={op}
                type="button"
                className={`sala-status__opcao ${atual === op ? 'sala-status__opcao--on' : ''}`}
                onClick={() => escolher(op)}
                disabled={salvando}
              >
                <i className={`bi ${atual === op ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                {op}
              </button>
            ))}
            {atual && (
              <button
                type="button"
                className="sala-status__opcao sala-status__opcao--limpar"
                onClick={() => escolher(null)}
                disabled={salvando}
              >
                <i className="bi bi-x-circle" /> Limpar status
              </button>
            )}
          </div>
        </>
      )}

      <button
        type="button"
        className={`sala-controle ${aberto ? 'sala-controle--ativo' : ''}`}
        onClick={() => setAberto((v) => !v)}
        title="Trocar meu status"
      >
        <i className={`bi ${atual ? 'bi-circle-fill' : 'bi-circle'}`} />
        <span className="sala-status__rotulo">{atual || 'Status'}</span>
      </button>
    </div>
  );
}

// Painel de chat. O historico e persistente (vive em dbo.siti_sala_mensagem),
// entao ao entrar na sala voce ja pega tudo que foi dito antes.
function PainelChat({ mensagens, carregando, meuId, onEnviar, onFechar, maxTexto }) {
  const [texto, setTexto] = useState('');
  const fimRef = useRef(null);

  // Cola no fim a cada mensagem nova.
  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'end' });
  }, [mensagens.length]);

  const submeter = (e) => {
    e.preventDefault();
    const limpo = texto.trim();
    if (!limpo) return;
    onEnviar(limpo);
    setTexto('');
  };

  return (
    <aside className="sala-chat">
      <header className="sala-chat__topo">
        <strong><i className="bi bi-chat-left-text" /> Chat da Geral</strong>
        <button type="button" onClick={onFechar} title="Fechar chat" aria-label="Fechar chat">
          <i className="bi bi-x-lg" />
        </button>
      </header>

      <div className="sala-chat__lista">
        {carregando && <p className="sala-chat__aviso">Carregando histórico...</p>}
        {!carregando && mensagens.length === 0 && (
          <p className="sala-chat__aviso">Nenhuma mensagem ainda. O histórico fica salvo.</p>
        )}
        {mensagens.map((m) => {
          const minha = String(m.usuario_id) === String(meuId);
          return (
            <div key={m.id} className={`sala-msg ${minha ? 'sala-msg--minha' : ''}`}>
              <div className="sala-msg__cabecalho">
                <span className="sala-msg__autor">{minha ? 'Você' : m.usuario_nome}</span>
                <span className="sala-msg__hora">{horaMensagem(m.criado_em)}</span>
              </div>
              <div className="sala-msg__texto">{m.texto}</div>
            </div>
          );
        })}
        <div ref={fimRef} />
      </div>

      <form className="sala-chat__envio" onSubmit={submeter}>
        <input
          type="text"
          value={texto}
          maxLength={maxTexto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escreva uma mensagem"
          aria-label="Mensagem"
        />
        <button type="submit" disabled={!texto.trim()} title="Enviar" aria-label="Enviar">
          <i className="bi bi-send-fill" />
        </button>
      </form>
    </aside>
  );
}

// Escolha de microfone, camera e saida de audio. Os rotulos so aparecem
// depois da permissao de midia — por isso este painel so existe dentro da
// chamada.
function CorpoDispositivos({
  dispositivos, configCamera, micAtual, saidaAudio, onTrocar, onSaida,
}) {
  const suportaSaida = typeof document.createElement('video').setSinkId === 'function';
  const idAtual = (lista, id) => (lista.some((d) => d.id === id) ? id : '');

  const linha = (rotulo, icone, lista, valor, aoMudar, vazio) => (
    <label className="sala-config__campo" key={rotulo}>
      <span><i className={`bi ${icone}`} /> {rotulo}</span>
      {lista.length === 0 ? (
        <em className="sala-config__vazio">{vazio}</em>
      ) : (
        <select value={valor} onChange={(e) => aoMudar(e.target.value)}>
          {!valor && <option value="">Padrão do sistema</option>}
          {lista.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
        </select>
      )}
    </label>
  );

  return (
    <>
      {linha('Microfone', 'bi-mic-fill', dispositivos.microfones, idAtual(dispositivos.microfones, micAtual),
        (id) => onTrocar('microfone', id), 'Nenhum microfone encontrado')}

      {linha('Câmera', 'bi-camera-video-fill', dispositivos.cameras,
        idAtual(dispositivos.cameras, configCamera?.deviceId),
        (id) => onTrocar('camera', id), 'Nenhuma câmera encontrada')}

      {suportaSaida
        ? linha('Saída de áudio', 'bi-volume-up-fill', dispositivos.saidas,
          idAtual(dispositivos.saidas, saidaAudio), onSaida, 'Nenhuma saída listada')
        : (
          <p className="sala-config__nota">
            <i className="bi bi-info-circle" />
            <span>
              Este navegador não deixa escolher a saída de áudio — o som vai
              para o dispositivo padrão do sistema.
            </span>
          </p>
        )}

      {configCamera && (
        <p className="sala-config__nota">
          <i className="bi bi-aspect-ratio" />
          <span>
            Sua câmera está entregando{' '}
            <strong>{configCamera.width}x{configCamera.height}</strong>
            {configCamera.frameRate ? ` a ${Math.round(configCamera.frameRate)} fps` : ''}.
            {' '}Se a imagem parecer aproximada, é o campo de visão da própria
            câmera: o portal pede a resolução nativa justamente para não recortar.
          </span>
        </p>
      )}
    </>
  );
}

// Personalizacao da propria sala. So aparece para o dono — a rota do backend
// tambem deriva o alvo do token, entao ninguem edita a sala de outro nem
// forcando a requisicao.
function CorpoMinhaSala({ sala, opcoes, onSalvar, onFechar }) {
  const [nome, setNome] = useState(sala.nome || '');
  const [cor, setCor] = useState(sala.cor || '');
  const [icone, setIcone] = useState(sala.icone || '');
  const [recado, setRecado] = useState(sala.recado || '');
  const [frase, setFrase] = useState(sala.frase || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const submeter = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    try {
      await onSalvar({
        nome: nome.trim() || null,
        cor: cor || null,
        icone: icone || null,
        recado: recado.trim() || null,
        frase: frase.trim() || null,
      });
      onFechar();
    } catch (_) {
      setErro('Não foi possível salvar. Tente de novo.');
      setSalvando(false);
    }
  };

  const maxRecado = opcoes?.tamanhoMaxRecado || 80;
  const maxFrase = opcoes?.tamanhoMaxFrase || 160;

  return (
    <form className="sala-config__form" onSubmit={submeter} style={estiloDaCor(cor)}>

        <label className="sala-config__campo">
          <span><i className="bi bi-card-text" /> Nome da sala</span>
          <input
            type="text"
            value={nome}
            maxLength={opcoes?.tamanhoMaxNome || 60}
            placeholder={sala.nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </label>

        <div className="sala-config__campo">
          <span><i className="bi bi-droplet-half" /> Cor</span>
          <div className="sala-cores">
            {(opcoes?.cores || Object.keys(CORES_DA_SALA)).map((chave) => (
              <button
                key={chave}
                type="button"
                className={`sala-cores__opcao ${cor === chave ? 'sala-cores__opcao--on' : ''}`}
                style={{ background: CORES_DA_SALA[chave]?.forte }}
                onClick={() => setCor(chave)}
                title={CORES_DA_SALA[chave]?.nome || chave}
                aria-label={CORES_DA_SALA[chave]?.nome || chave}
              />
            ))}
          </div>
        </div>

        <div className="sala-config__campo">
          <span>
            <i className="bi bi-emoji-smile" /> Ícone
            {icone && <IconeSala icone={icone} className="sala-icones__atual" />}
          </span>
          <div className="sala-icones">
            {(opcoes?.emojis || []).map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={`sala-icones__opcao ${icone === emoji ? 'sala-icones__opcao--on' : ''}`}
                onClick={() => setIcone(icone === emoji ? '' : emoji)}
                aria-label={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
          {/* A grade e so sugestao: vale QUALQUER emoji. No Mac, Ctrl+Cmd+Espaço
              abre o seletor do sistema. */}
          <input
            type="text"
            className="sala-icones__livre"
            value={icone.startsWith('bi-') ? '' : icone}
            maxLength={16}
            placeholder="ou cole qualquer emoji aqui"
            onChange={(e) => setIcone(e.target.value.trim())}
          />
        </div>

        <label className="sala-config__campo">
          <span><i className="bi bi-broadcast-pin" /> Status ({recado.length}/{maxRecado})</span>
          <input
            type="text"
            value={recado}
            maxLength={maxRecado}
            placeholder="disponível"
            onChange={(e) => setRecado(e.target.value)}
          />
          {/* Sugestoes de um clique. Nao substituem o texto livre — so evitam
              digitar o de sempre. */}
          <div className="sala-sugestoes">
            {(opcoes?.statusSugeridos || []).map((sugestao) => (
              <button
                key={sugestao}
                type="button"
                className={`sala-sugestoes__item ${recado === sugestao ? 'sala-sugestoes__item--on' : ''}`}
                onClick={() => setRecado(recado === sugestao ? '' : sugestao)}
              >
                {sugestao}
              </button>
            ))}
          </div>
        </label>

        <label className="sala-config__campo">
          <span><i className="bi bi-quote" /> Frase ({frase.length}/{maxFrase})</span>
          <input
            type="text"
            value={frase}
            maxLength={maxFrase}
            placeholder="algo que represente você"
            onChange={(e) => setFrase(e.target.value)}
          />
          <em className="sala-config__ajuda">
            Aparece no topo para quem entra na sua sala.
          </em>
        </label>

        {erro && <p className="sala-config__nota"><i className="bi bi-exclamation-triangle" /><span>{erro}</span></p>}

      <div className="sala-config__acoes">
        <button type="button" className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button type="submit" className="btn btn-primary" disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}


// Um unico painel de Configuracoes, com abas. Antes eram dois botoes na barra
// ("Minha sala" e "Dispositivos") disputando espaco com os controles de
// chamada. `abas` decide o que aparece: na tela inicial nao ha midia ativa,
// entao so faz sentido "Minha sala".
function PainelConfiguracoes({ abas, aba, onAba, onFechar, minhaSala, dispositivos }) {
  const disponiveis = abas.filter((a) => a.id !== 'sala' || !!minhaSala);
  const ativa = disponiveis.some((a) => a.id === aba) ? aba : disponiveis[0]?.id;

  // Onde o botao FOI PRESSIONADO, nao onde foi solto.
  //
  // Fechar no `click` do fundo parece certo, mas selecionar texto dentro do
  // modal e soltar o mouse do lado de fora dispara um click no fundo — e o
  // formulario fechava no meio da edicao. So fecha se o gesto comecou E
  // terminou no fundo.
  const origemDoClique = useRef(null);

  const talvezFechar = (e) => {
    if (e.target === e.currentTarget && origemDoClique.current === e.currentTarget) {
      onFechar();
    }
    origemDoClique.current = null;
  };

  return (
    <div
      className="sala-config__fundo"
      onMouseDown={(e) => { origemDoClique.current = e.target; }}
      onClick={talvezFechar}
      role="presentation"
    >
      <div className="sala-config" role="dialog" aria-label="Configurações">
        <header className="sala-config__topo">
          <strong><i className="bi bi-sliders" /> Configurações</strong>
          <button type="button" onClick={onFechar} title="Fechar" aria-label="Fechar">
            <i className="bi bi-x-lg" />
          </button>
        </header>

        {disponiveis.length > 1 && (
          <div className="sala-abas" role="tablist">
            {disponiveis.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={ativa === a.id}
                className={`sala-abas__item ${ativa === a.id ? 'sala-abas__item--on' : ''}`}
                onClick={() => onAba(a.id)}
              >
                <i className={`bi ${a.icone}`} /> {a.rotulo}
              </button>
            ))}
          </div>
        )}

        {ativa === 'sala' && minhaSala && <CorpoMinhaSala {...minhaSala} onFechar={onFechar} />}
        {ativa === 'dispositivos' && <CorpoDispositivos {...dispositivos} />}
      </div>
    </div>
  );
}

const ABAS_CONFIG = [
  { id: 'sala', rotulo: 'Minha sala', icone: 'bi-palette' },
  { id: 'dispositivos', rotulo: 'Dispositivos', icone: 'bi-mic' },
];

export default function Sala() {
  const { user } = useAuth();
  const [presenca, setPresenca] = useState({ salas: [], limitePorSala: 5, limiteVideoAlta: 3 });
  const [loading, setLoading] = useState(true);
  const [erroPresenca, setErroPresenca] = useState('');
  const [palcoRef, palco] = useTamanho();
  // Tile fixado a mao. Tem prioridade sobre o destaque automatico de quem
  // esta apresentando.
  const [fixado, setFixado] = useState(null);
  const [chatAberto, setChatAberto] = useState(false);
  // Id da ultima mensagem lida. Persistido por usuario — sem isso o historico
  // inteiro voltava como "nao lido" a cada carregamento da pagina.
  const [lidoAte, setLidoAte] = useState(() => lerMarcaDeLeitura(user?.id));
  // null = painel fechado. Guarda QUAL aba abrir, para o botao da tela
  // inicial cair direto em "Minha sala".
  const [abaConfig, setAbaConfig] = useState(null);
  const [opcoesPersonalizacao, setOpcoes] = useState(null);

  const {
    status, sala, participantes, streamLocal, streamTela,
    mudo, cameraDesligada, compartilhandoTela, erro, aviso,
    entrar, sair, trocarSala, trocandoPara,
    alternarMudo, alternarCamera, alternarTela,
    configCamera, dispositivos, saidaAudio, definirSaidaAudio, trocarDispositivo,
    mensagens, chatCarregando, enviarMensagem,
    setLimiteVideoAlta,
    audioAjustes: audioPorUsuario,
    atualizarAudioParticipante,
  } = useSala();

  // Sala.js e quem carrega a presenca; o limite so chega depois. Sincroniza
  // com o Provider para que o degrade automatico de qualidade use o teto real.
  useEffect(() => {
    setLimiteVideoAlta(presenca.limiteVideoAlta);
  }, [presenca.limiteVideoAlta, setLimiteVideoAlta]);

  const naSala = status === 'na-sala';

  // Trocar de sala (ou sair) zera o destaque manual: os tiles sao outros.
  useEffect(() => { setFixado(null); }, [sala?.id]);

  // Com o chat aberto, tudo que chega ja nasce lido — inclusive o que chega
  // enquanto ele fica aberto.
  useEffect(() => {
    if (!chatAberto || !user?.id) return;
    const ultimo = ultimoId(mensagens);
    if (ultimo <= 0) return;
    gravarMarcaDeLeitura(user.id, ultimo);
    setLidoAte((antes) => (ultimo > antes ? ultimo : antes));
  }, [chatAberto, mensagens, user?.id]);

  const carregarPresenca = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sala/salas`);
      if (res.data?.data) setPresenca(res.data.data);
      setErroPresenca('');
    } catch {
      setErroPresenca('Não foi possível carregar as salas.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Roda tambem dentro da chamada: e o que alimenta os contadores do seletor
  // de sala no topo (saber se a outra sala tem gente antes de pular pra la).
  useEffect(() => {
    carregarPresenca();
    const id = setInterval(carregarPresenca, INTERVALO_PRESENCA_MS);
    return () => clearInterval(id);
  }, [carregarPresenca]);

  // Catalogo de cores/icones aceitos. Vem do backend para nao duplicar a
  // lista dos dois lados — quem valida e ele.
  useEffect(() => {
    let ativo = true;
    axios.get(`${API}/sala/personalizacao/opcoes`)
      .then((res) => { if (ativo) setOpcoes(res.data?.data || null); })
      .catch(() => { /* seletor cai nos padroes do frontend */ });
    return () => { ativo = false; };
  }, []);

  const salvarPersonalizacao = useCallback(async (dados) => {
    await axios.put(`${API}/sala/minha/config`, dados);
    await carregarPresenca();
  }, [carregarPresenca]);

  // A sala pessoal do usuario logado, para saber o que ele pode personalizar.
  const minhaSala = presenca.salas.find((s2) => s2.pessoal && String(s2.donoId) === String(user?.id));

  const handleEntrar = (salaId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    entrar(salaId, token);
  };

  const handleSair = () => {
    sair();
    carregarPresenca();
  };

  const alternarFixado = useCallback((chave) => {
    setFixado((prev) => (prev === chave ? null : chave));
  }, []);

  // Ajustes por usuario vem do SalaProvider (compartilhado com o SalaAudioSink).
  const ajustarAudio = useCallback((usuarioId, patch) => {
    atualizarAudioParticipante(usuarioId, patch);
  }, [atualizarAudioParticipante]);

  // --- Dentro da sala ---
  if (naSala) {
    const meuNome = user?.nome || 'Voce';

    // Camera e tela sao tiles SEPARADOS: quem apresenta continua aparecendo.
    const tiles = [{
      chave: 'local',
      tipo: 'camera',
      local: true,
      nome: `${meuNome} (você)`,
      stream: streamLocal,
      mudo,
      cameraDesligada,
      conexao: 'connected',
    }];

    if (compartilhandoTela) {
      tiles.push({
        chave: 'local-tela',
        tipo: 'tela',
        local: true,
        nome: 'Sua tela',
        stream: streamTela,
        conexao: 'connected',
      });
    }

    participantes.forEach((p) => {
      tiles.push({
        chave: p.peerId,
        usuarioId: p.usuarioId,
        tipo: 'camera',
        local: false,
        nome: p.nome,
        stream: p.stream,
        mudo: p.mudo,
        cameraDesligada: p.cameraDesligada,
        conexao: p.conexao,
        rota: p.rota,
      });
      if (p.compartilhandoTela) {
        tiles.push({
          chave: `${p.peerId}-tela`,
          tipo: 'tela',
          local: false,
          nome: `Tela de ${p.nome}`,
          stream: p.streamTela,
          conexao: p.conexao,
        });
      }
    });

    // Destaque: o pin manual manda; sem pin, quem estiver apresentando sobe
    // sozinho para o palco (comportamento de sala de reuniao).
    const apresentando = tiles.find((t) => t.tipo === 'tela');
    const fixadoValido = fixado && tiles.some((t) => t.chave === fixado) ? fixado : null;
    const chaveDestaque = fixadoValido || apresentando?.chave || null;
    const emDestaque = chaveDestaque ? tiles.find((t) => t.chave === chaveDestaque) : null;
    const miniaturas = emDestaque ? tiles.filter((t) => t.chave !== chaveDestaque) : [];

    const destaque = emDestaque
      ? calcularDestaque(miniaturas.length, palco.largura, palco.altura)
      : null;
    const grade = emDestaque ? null : calcularGrade(tiles.length, palco.largura, palco.altura);

    const total = participantes.length + 1;
    const oculto = { display: 'none' };
    const temChat = !!sala?.chat;
    // O objeto que vem pelo WebSocket ao entrar e um retrato do momento. A
    // presenca (polling de 5s) traz a versao viva — cor, frase e status
    // mudam sem precisar sair e entrar.
    const salaAtualCompleta = presenca.salas.find((x) => x.id === sala?.id) || sala;
    const naoLidas = chatAberto ? 0 : contarNaoLidas(mensagens, user?.id, lidoAte);
    const micAtual = streamLocal?.getAudioTracks?.()[0]?.getSettings?.().deviceId || '';
    // Selo com a resolucao REAL da camera, so no proprio tile.
    const seloCamera = configCamera?.width
      ? `${configCamera.width}x${configCamera.height}`
      : null;

    return (
      <div className="sala-shell">
        <div className="sala-topo">
          <BarraDeSalas
            salas={presenca.salas}
            limite={presenca.limitePorSala}
            salaAtiva={sala?.id}
            alvo={trocandoPara}
            onIr={trocarSala}
            meuId={user?.id}
          />
          {/* A frase e do DONO da sala em que voce esta — e o "dizer para o
              mundo" de quem recebe. A Geral nao tem dono, entao fica VAZIA de
              proposito: o chip ativo ja diz onde voce esta e quantos sao, e uma
              descricao fixa vira ruido em quem le a mesma tela o dia inteiro.
              O <p> continua renderizado mesmo vazio porque e ele que empurra o
              status para a direita (flex: 1). Fica a direita para os chips
              ficarem colados na esquerda, onde a leitura comeca. */}
          <p className="sala-topo__frase">{salaAtualCompleta?.frase || ''}</p>
          {/* So aparece quando tem o que dizer. Vazio, o gap do topo abriria um
              buraco a direita sem nada dentro. A contagem de gente saiu daqui:
              o chip da sala ja mostra "3/5". */}
          {(fixadoValido || total > presenca.limiteVideoAlta) && (
            <div className="sala-topo__status">
              {fixadoValido && (
                <button type="button" className="sala-topo__desafixar" onClick={() => setFixado(null)}>
                  <i className="bi bi-pin-fill" /> desafixar
                </button>
              )}
              {total > presenca.limiteVideoAlta && (
                <span className="sala-topo__aviso" title="Malha com 4+ pessoas: cada navegador codifica um envio a mais por participante">
                  <i className="bi bi-speedometer" /> qualidade reduzida
                </span>
              )}
            </div>
          )}
        </div>

        {(erro || aviso) && (
          <div className={`sala-faixa ${erro ? 'sala-faixa--erro' : ''}`}>
            <i className={`bi ${erro ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'}`} />
            {erro || aviso}
          </div>
        )}

        <div className="sala-corpo" style={estiloDaCor(salaAtualCompleta?.cor)}>
        <div
          className={`sala-palco ${emDestaque ? `sala-palco--destaque sala-palco--${destaque?.orientacao || 'nenhuma'}` : ''}`}
          ref={palcoRef}
        >
          {emDestaque ? (
            <>
              <div className="sala-destaque">
                <VideoTile
                  tile={emDestaque}
                  estilo={destaque ? { width: destaque.principal.largura, height: destaque.principal.altura } : oculto}
                  fixado={fixadoValido === emDestaque.chave}
                  onFixar={alternarFixado}
                  saidaAudio={saidaAudio}
                  selo={emDestaque.chave === 'local' ? seloCamera : null}
                  audio={audioPorUsuario[emDestaque.usuarioId]}
                  onAudio={ajustarAudio}
                />
              </div>
              {miniaturas.length > 0 && destaque?.miniatura && (
                <div className="sala-minis" style={{ gap: GAP }}>
                  {miniaturas.map((t) => (
                    <VideoTile
                      key={t.chave}
                      compacto
                      tile={t}
                      estilo={{ width: destaque.miniatura.largura, height: destaque.miniatura.altura }}
                      fixado={false}
                      onFixar={alternarFixado}
                      saidaAudio={saidaAudio}
                      audio={audioPorUsuario[t.usuarioId]}
                      onAudio={ajustarAudio}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="sala-grid" style={grade ? { width: grade.larguraFaixa, gap: GAP } : undefined}>
              {tiles.map((t) => (
                <VideoTile
                  key={t.chave}
                  tile={t}
                  estilo={grade ? { width: grade.largura, height: grade.altura } : oculto}
                  fixado={false}
                  onFixar={alternarFixado}
                  saidaAudio={saidaAudio}
                  selo={t.chave === 'local' ? seloCamera : null}
                  audio={audioPorUsuario[t.usuarioId]}
                  onAudio={ajustarAudio}
                />
              ))}
            </div>
          )}
        </div>

        {temChat && chatAberto && (
          <PainelChat
            mensagens={mensagens}
            carregando={chatCarregando}
            meuId={user?.id}
            maxTexto={2000}
            onEnviar={enviarMensagem}
            onFechar={() => setChatAberto(false)}
          />
        )}
        </div>

        <div className="sala-controles">
          <button
            type="button"
            className={`sala-controle ${mudo ? 'sala-controle--off' : ''}`}
            onClick={alternarMudo}
            title={mudo ? 'Ativar microfone' : 'Silenciar microfone'}
          >
            <i className={`bi ${mudo ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`} />
            <span>{mudo ? 'Mudo' : 'Microfone'}</span>
          </button>

          <button
            type="button"
            className={`sala-controle ${cameraDesligada ? 'sala-controle--off' : ''}`}
            onClick={alternarCamera}
            disabled={!streamLocal?.getVideoTracks().length}
            title="Ligar/desligar câmera"
          >
            <i className={`bi ${cameraDesligada ? 'bi-camera-video-off-fill' : 'bi-camera-video-fill'}`} />
            <span>{cameraDesligada ? 'Câmera off' : 'Câmera'}</span>
          </button>

          <button
            type="button"
            className={`sala-controle ${compartilhandoTela ? 'sala-controle--ativo' : ''}`}
            onClick={alternarTela}
            title={compartilhandoTela ? 'Parar de compartilhar' : 'Compartilhar tela'}
          >
            <i className="bi bi-display-fill" />
            <span>{compartilhandoTela ? 'Parar' : 'Tela'}</span>
          </button>

          {minhaSala && (
            <StatusRapido
              atual={minhaSala.recado}
              sugestoes={opcoesPersonalizacao?.statusSugeridos}
              onEscolher={(valor) => salvarPersonalizacao({ recado: valor })}
            />
          )}

          {temChat && (
            <button
              type="button"
              className={`sala-controle ${chatAberto ? 'sala-controle--ativo' : ''}`}
              onClick={() => setChatAberto((v) => !v)}
              title="Chat da sala"
            >
              <span className="sala-controle__icone">
                <i className="bi bi-chat-left-text-fill" />
                {naoLidas > 0 && <span className="sala-controle__badge">{naoLidas > 9 ? '9+' : naoLidas}</span>}
              </span>
              <span>Chat</span>
            </button>
          )}

          <button
            type="button"
            className={`sala-controle ${abaConfig ? 'sala-controle--ativo' : ''}`}
            onClick={() => setAbaConfig(abaConfig ? null : (minhaSala ? 'sala' : 'dispositivos'))}
            title="Minha sala e dispositivos"
          >
            <i className="bi bi-gear-fill" />
            <span>Configurações</span>
          </button>

          <button
            type="button"
            className="sala-controle sala-controle--sair"
            onClick={handleSair}
            title="Sair da sala"
          >
            <i className="bi bi-telephone-x-fill" />
            <span>Sair</span>
          </button>
        </div>

        {abaConfig && (
          <PainelConfiguracoes
            abas={ABAS_CONFIG}
            aba={abaConfig}
            onAba={setAbaConfig}
            onFechar={() => setAbaConfig(null)}
            minhaSala={minhaSala && {
              sala: minhaSala,
              opcoes: opcoesPersonalizacao,
              onSalvar: salvarPersonalizacao,
            }}
            dispositivos={{
              dispositivos,
              configCamera,
              micAtual,
              saidaAudio,
              onTrocar: trocarDispositivo,
              onSaida: definirSaidaAudio,
            }}
          />
        )}
      </div>
    );
  }

  // --- Lobby ---
  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Sala de voz</h1>
          <p>Chamada de voz e vídeo da equipe. Entre numa sala e fale com quem já estiver lá.</p>
        </div>
      </section>

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {erroPresenca && <div className="feedback-banner feedback-banner--error">{erroPresenca}</div>}

      {loading ? (
        <section className="content-card">
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando salas...</p>
          </div>
        </section>
      ) : (
        <div className="sala-lobby">
          {presenca.salas.map((s) => {
            const cheia = s.participantes.length >= presenca.limitePorSala;
            const minha = s.pessoal && String(s.donoId) === String(user?.id);
            return (
              <div
                key={s.id}
                className={`sala-card ${s.cor ? 'sala-card--colorida' : ''} ${minha ? 'sala-card--minha' : ''}`}
                style={estiloDaCor(s.cor)}
              >
                <div className="sala-card__topo">
                  <IconeSala icone={s.icone} className="sala-card__icone" />
                  <div>
                    <strong>
                      {s.nome}
                      {minha && <span className="sala-card__selo">sua sala</span>}
                    </strong>
                    <p>{s.descricao}</p>
                    {s.pessoal && s.frase && <p className="sala-card__frase">“{s.frase}”</p>}
                  </div>
                  <span className={`sala-card__contador ${s.participantes.length ? 'sala-card__contador--ativo' : ''}`}>
                    {s.participantes.length}/{presenca.limitePorSala}
                  </span>
                </div>

                <div className="sala-card__pessoas">
                  {s.participantes.length === 0 ? (
                    <span className="sala-card__vazia">Ninguém por aqui agora.</span>
                  ) : (
                    s.participantes.map((p) => (
                      <span key={p.peerId} className="sala-card__pessoa" title={p.nome}>
                        <span className="sala-card__pessoa-avatar">{iniciais(p.nome)}</span>
                        {p.nome}
                        {p.mudo && <i className="bi bi-mic-mute-fill" style={{ opacity: 0.7 }} />}
                        {p.compartilhandoTela && <i className="bi bi-display-fill" style={{ color: '#3CA8BC' }} />}
                      </span>
                    ))
                  )}
                </div>

                <div className="sala-card__acoes">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleEntrar(s.id)}
                    disabled={cheia || status === 'conectando'}
                  >
                    <i className="bi bi-camera-video-fill" />
                    {cheia
                      ? ` Sala cheia (${presenca.limitePorSala})`
                      : status === 'conectando' ? ' Entrando...' : ' Entrar'}
                  </button>
                  {minha && (
                    <button
                      type="button"
                      className="btn btn-secondary sala-card__personalizar"
                      onClick={() => setAbaConfig('sala')}
                      title="Personalizar minha sala"
                    >
                      <i className="bi bi-palette" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="sala-nota">
        <i className="bi bi-info-circle" />
        A conversa é ponto a ponto entre os navegadores — o servidor só apresenta
        os participantes. Limite de {presenca.limitePorSala} pessoas por sala.
      </p>

      {abaConfig && minhaSala && (
        <PainelConfiguracoes
          abas={ABAS_CONFIG.filter((a) => a.id === 'sala')}
          aba="sala"
          onAba={setAbaConfig}
          onFechar={() => setAbaConfig(null)}
          minhaSala={{
            sala: minhaSala,
            opcoes: opcoesPersonalizacao,
            onSalvar: salvarPersonalizacao,
          }}
        />
      )}
    </div>
  );
}
